import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MoreHorizontal, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Loading } from '../../components/office';
import { purchaseOrdersApi, stockApi, suppliersApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { SearchField } from '@verlo/shared/components/ui/search-field';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@verlo/shared/components/ui/dropdown-menu';
import { useAuthoringSite, useSite } from '../../contexts/SiteContext';
import {
  add, againstPar, draftBody, emptyBasket, firstQuantity, fromBundle, fromSuggestions, grouped, packOf, remove, search, step, toPack,
} from '../../lib/basket';
import { onOrderWords, qty } from '../../lib/orders';
import { UNITS } from '../../lib/units';

// The order builder (Oct 2026, mock "Ordering from suppliers"). Every way into ordering opens
// it: Review and order on Today, Order these on Orders (both filled with what's below par), a
// bundle's Order (filled with the bundle), and New order (empty). The whole order is shaped
// here, across suppliers: quantities in their packs, a line taken off, an item no longer
// bought from them, a product they've just started selling. Then Create drafts makes one
// draft per supplier, asking first where a supplier already has one open. The basket is kept
// in this browser until it's drafted or cleared.

// "6 bottles", "1 keg", "4 kg": a counted unit takes an s, a measure doesn't.
const MEASURES = new Set(['kg', 'g', 'l', 'ml', 'litre', 'litres', 'L', 'each']);
const amount = (n, unit) => {
  const v = Math.round(Number(n || 0) * 1000) / 1000;
  if (!unit || unit === 'each' || /^case of/i.test(unit)) return qty(v, unit);
  return `${v} ${!MEASURES.has(unit) && v !== 1 && !unit.endsWith('s') ? `${unit}s` : unit}`;
};
const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);
const KEY = (site) => `verlo.orderBasket.${site || 'all'}`;
const readSaved = (site) => { try { const b = JSON.parse(localStorage.getItem(KEY(site)) || 'null'); return b?.lines ? b : null; } catch { return null; } };
const writeSaved = (site, b) => { try { if (b?.lines?.length) localStorage.setItem(KEY(site), JSON.stringify(b)); else localStorage.removeItem(KEY(site)); } catch { /* this browser keeps nothing */ } };

export default function OrderBuilderPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { site: picked } = useSite();
  const authoring = useAuthoringSite();
  const site = authoring.site || picked || null;
  const [stock, setStock] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [basket, setBasket] = useState(null);
  const [carriedOn, setCarriedOn] = useState(false);   // a saved basket was opened rather than a fresh one
  const [term, setTerm] = useState('');
  const [stopping, setStopping] = useState(null);      // { item, to } while asking Stop buying
  const [unstocking, setUnstocking] = useState(null);  // the item, while asking Stop stocking
  const [making, setMaking] = useState(null);          // the New product form
  const [asking, setAsking] = useState(null);          // { supplier id: draft id | 'new' } while asking about open drafts
  const [busy, setBusy] = useState(false);
  const [then, setThen] = useState('send');   // after the drafts: on to Review and send, or kept
  const [onOrder, setOnOrder] = useState({});  // stock id → { quantity, due } already on open orders
  const started = useRef(false);

  const loadStock = () => stockApi.getAll().then((r) => setStock((r.data || []).filter((s) => !site || !s.site_id || s.site_id === site)));
  useEffect(() => {
    Promise.all([
      stockApi.getAll(), suppliersApi.getAll().catch(() => ({ data: [] })), purchaseOrdersApi.list(site || undefined),
      purchaseOrdersApi.suggestions(site || undefined).catch(() => ({ data: null })),
    ]).then(async ([s, sup, o, sug]) => {
      setOnOrder(sug.data?.on_order || {});
      const mine = (s.data || []).filter((x) => !site || !x.site_id || x.site_id === site);
      setStock(mine);
      setSuppliers((sup.data || []).filter((x) => x.status !== 'inactive'));
      setDrafts((o.data || []).filter((d) => d.status === 'draft'));
      if (started.current) return;
      started.current = true;
      // A basket already started is carried on, whichever way in; a fresh one is filled from
      // what this way in asked for.
      const saved = readSaved(site);
      const from = params.get('from');
      const bundleId = params.get('bundle');
      if (saved && (from || bundleId)) { setBasket(saved); setCarriedOn(true); return; }
      if (saved) { setBasket(saved); return; }
      if (from === 'par') {
        setBasket(fromSuggestions(sug.data?.groups));
      } else if (bundleId) {
        const { data } = await purchaseOrdersApi.bundles();
        setBasket(fromBundle((data || []).find((b) => b.id === bundleId), mine));
      } else setBasket(emptyBasket());
    }).catch(() => toast.error("Couldn't load what's needed to order"));
  }, [site]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (basket) writeSaved(site, basket); }, [basket, site]);

  const stockById = useMemo(() => Object.fromEntries((stock || []).map((s) => [s.id, s])), [stock]);
  const supById = useMemo(() => Object.fromEntries(suppliers.map((s) => [s.id, s])), [suppliers]);
  const view = useMemo(() => (basket ? grouped(basket, stockById, supById, drafts) : null), [basket, stockById, supById, drafts]);
  const found = useMemo(() => search(term, stock, suppliers), [term, stock, suppliers]);
  const categories = useMemo(() => [...new Set((stock || []).map((s) => s.category).filter(Boolean))].sort(), [stock]);

  if (!stock || !basket || !view) return <Loading />;

  // − past the last pack takes it off; Undo puts it back as it was.
  const less = (l) => {
    const before = basket;
    const after = step(basket, l.item, -1);
    setBasket(after);
    if (after.lines.length < before.lines.length) toast(`${l.item.name} is off the order`, { action: { label: 'Undo', onClick: () => setBasket(before) } });
  };
  const addItem = (item) => { setBasket((b) => add(b, item, firstQuantity(item, onOrder[item.id]?.quantity))); setTerm(''); };
  const fromPar = async () => {
    const { data } = await purchaseOrdersApi.suggestions(site || undefined);
    setOnOrder(data.on_order || {});
    if (!data.groups.some((g) => g.supplier_id)) toast.info("Nothing's below par that isn't already on order");
    setBasket(fromSuggestions(data.groups));
    setCarriedOn(false);
  };
  const startAgain = () => {
    if (basket.from === 'par' || carriedOn) return fromPar();
    setBasket(emptyBasket());
    setCarriedOn(false);
  };

  // Stop buying it from them: the supplier comes off the item (or another goes on), for good.
  const stopBuying = async () => {
    const { item, to } = stopping;
    setBusy(true);
    try {
      await stockApi.patch(item.id, { supplier_id: to || null });
      if (!to) setBasket((b) => remove(b, item.id));
      await loadStock();
      toast.success(to ? `${item.name} now comes from ${supById[to]?.name}` : `${item.name} won't be ordered from ${supById[item.supplier_id]?.name} again`);
      setStopping(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change who it's bought from"); } finally { setBusy(false); }
  };
  // Stop stocking it: par to 0, so it's never on the to-order list.
  const stopStocking = async () => {
    const item = unstocking;
    setBusy(true);
    try {
      await stockApi.patch(item.id, { par_level: 0 });
      setBasket((b) => remove(b, item.id));
      await loadStock();
      toast.success(`${item.name} is off the to-order list`);
      setUnstocking(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change it"); } finally { setBusy(false); }
  };
  // A product the supplier has just started selling: a stock item, at 0, with them, and on
  // the order. Par is optional; it can be set later on Stock levels.
  const makeProduct = async () => {
    const f = making;
    const price = parseFloat(f.price);
    const pack = parseFloat(f.pack_size) || 1;
    const par = f.par_level === '' ? 0 : parseFloat(f.par_level);
    if (!f.name.trim() || !f.supplier_id || !f.unit) { toast.error('A name, who it\'s from and how you count it, please'); return; }
    if (!(price >= 0)) { toast.error('Their price, please'); return; }
    setBusy(true);
    try {
      const { data } = await stockApi.create({
        name: f.name.trim(), quantity: 0, unit: f.unit, category: f.category || 'General',
        par_level: par > 0 ? par : 0, min_quantity: par > 0 ? Math.round((par / 2) * 1000) / 1000 : 0,
        cost_per_unit: price, supplier_id: f.supplier_id, pack_size: pack, order_code: f.order_code.trim(),
      }, site || undefined);
      await loadStock();
      setBasket((b) => add(b, data, Math.max(pack, toPack(f.order * pack, pack))));
      toast.success(`${data.name} is in stock, and on the order`);
      setMaking(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add it"); } finally { setBusy(false); }
  };
  const newProduct = (supplierId, name = '') => {
    const theirs = (stock || []).filter((s) => s.supplier_id === supplierId);
    const counts = theirs.reduce((m, s) => ({ ...m, [s.category]: (m[s.category] || 0) + 1 }), {});
    const category = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || '';
    setMaking({ supplier_id: supplierId || '', name, unit: 'each', pack_size: '1', price: '', order_code: '', par_level: '', category, order: 1 });
    setTerm('');
  };

  // The order's drafts, then on to Review and send (`then` 'send'), or kept for later
  // ('save'). Where a supplier already has a draft open, ask first (Martin, 26 Oct).
  const create = async (choices, next = then) => {
    setThen(next);
    const withDraft = view.groups.filter((g) => g.ordering && g.drafts.length);
    if (!choices && withDraft.length) {
      setAsking(Object.fromEntries(withDraft.map((g) => [g.supplier_id, g.drafts[0].id])));
      return;
    }
    setBusy(true);
    try {
      const { data } = await purchaseOrdersApi.basket(draftBody(view.groups, choices || {}), site || undefined);
      writeSaved(site, null);
      const n = data.drafts.length + data.added_to.length;
      if (next === 'send') {
        navigate(`/back-office/orders/send?ids=${[...data.drafts, ...data.added_to].map((d) => d.id).join(',')}`);
        return;
      }
      navigate('/back-office/orders', { state: { drafted: { made: data.drafts.length, added: data.added_to.map((d) => d.number), total: data.total ?? view.total } } });
      toast.success(`${n} order${n === 1 ? '' : 's'} saved as drafts, to send later`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft the orders");
      setAsking(null);
      // Whatever refused, the drafts this page is holding are out of date — a draft was
      // sent, or one was made before the refusal. Read them again so the next press offers
      // the real choice rather than drafting the same lines a second time.
      await purchaseOrdersApi.list(site || undefined)
        .then((r) => setDrafts((r.data || []).filter((d) => d.status === 'draft')))
        .catch(() => {});
    } finally { setBusy(false); }
  };

  const title = basket.from === 'par' ? "From what's below par" : basket.from === 'bundle' ? 'From a bundle' : null;
  // Only suppliers with something ordered get a draft; one at 0 throughout is skipped.
  const sending = view.groups.filter((g) => g.ordering);
  const n = sending.length;

  return (
    <div className="space-y-4" data-testid="order-builder">
      <Link to="/back-office/orders" className="text-[13px] text-ink-soft hover:text-ink">‹ Orders</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-[26px] font-extrabold text-ink">New order</h1>
        {title && <span className="rounded-md border border-hairline bg-white px-2.5 py-1 text-[13px] text-ink-soft">{title}</span>}
      </div>
      {carriedOn && (
        <Notice tone="info" word="Carrying on" action={<Button size="sm" variant="outline" onClick={startAgain}>Start again from what&apos;s below par</Button>}>
          This is the order you started before. Nothing has been drafted from it yet.
        </Notice>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-3">
          <div className="relative">
            <SearchField value={term} onChange={(e) => setTerm(e.target.value)} onClear={() => setTerm('')} placeholder="Add anything: a product, or a supplier" className="h-10 bg-white" data-testid="builder-search" autoFocus={basket.lines.length === 0} />
            {term.trim() && (
              <div className="absolute left-0 right-0 top-11 z-20 overflow-hidden rounded-md border border-hairline-strong bg-white shadow-lg" data-testid="builder-results">
                {found.products.length > 0 && <p className="bg-surface-2 px-3 py-1.5 font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">Products</p>}
                {found.products.map((s) => (
                  <button key={s.id} type="button" className="flex w-full items-center justify-between gap-3 border-t border-hairline px-3 py-2 text-left hover:bg-hover-tint" onClick={() => addItem(s)} data-testid={`builder-result-${s.id}`}>
                    <span className="min-w-0"><span className="block truncate text-[14px] text-ink">{s.name}</span>
                      <span className="block text-[12px] text-ink-faint">{supById[s.supplier_id]?.name} · you have {s.par_level ? `${qty(s.quantity, '')} of ${amount(s.par_level, s.unit)}` : amount(s.quantity, s.unit)}</span></span>
                    <span className="shrink-0 font-mono text-[13px] tabular-nums text-ink-soft">{money(s.cost_per_unit)} / {s.unit}</span>
                  </button>
                ))}
                {found.suppliers.length > 0 && <p className="border-t border-hairline bg-surface-2 px-3 py-1.5 font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">Suppliers</p>}
                {found.suppliers.map((s) => (
                  <AddFrom key={s.id} supplier={s} stock={stock} basket={basket} onAdd={addItem} onNew={() => newProduct(s.id)} trigger={
                    <button type="button" className="block w-full border-t border-hairline px-3 py-2 text-left text-[14px] text-ink hover:bg-hover-tint">{s.name}<span className="block text-[12px] text-ink-faint">Their products</span></button>
                  } />
                ))}
                <button type="button" className="block w-full border-t border-hairline px-3 py-2 text-left text-[14px] font-semibold text-verlo hover:bg-hover-tint" onClick={() => newProduct('', term.trim())} data-testid="builder-new-from-search">
                  + New product called “{term.trim()}”<span className="block text-[12px] font-normal text-ink-faint">Choose who it&apos;s from next</span>
                </button>
              </div>
            )}
          </div>

          {n === 0 && (basket.from === 'par' && !basket.lines.length ? (
            // Below par, but all of it already on an order: say so, and where the drafts are.
            <div className="rounded-lg border border-hairline bg-white px-5 py-8 text-center" data-testid="builder-all-on-order">
              <p className="font-heading text-[16px] font-bold text-ink">Everything below par is on order</p>
              <p className="mt-1 text-[13.5px] text-ink-soft">
                {drafts.length > 0
                  ? <>{drafts.length} draft{drafts.length === 1 ? ' is' : 's are'} still to send. <button type="button" className="text-verlo hover:underline" onClick={() => navigate(`/back-office/orders/send?ids=${drafts.map((d) => d.id).join(',')}`)}>Review and send {drafts.length === 1 ? 'it' : 'them'}</button>, or search above to order something else.</>
                  : 'Search above to order something else.'}
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-hairline bg-white px-5 py-8 text-center">
              <p className="font-heading text-[16px] font-bold text-ink">Nothing on it yet</p>
              <p className="mt-1 text-[13.5px] text-ink-soft">Search for a product or a supplier above, or <button type="button" className="text-verlo hover:underline" onClick={fromPar} data-testid="builder-from-par">start from what&apos;s below par</button>.</p>
            </div>
          ))}

          {view.groups.map((g) => (
            <section key={g.supplier_id} className="rounded-lg border border-hairline bg-white" data-testid={`builder-group-${g.supplier_id}`}>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-hairline px-4 py-2.5">
                <h2 className="font-heading text-[15px] font-extrabold text-ink">{g.supplier_name}</h2>
                <span className="text-[13px] text-ink-soft">{g.ordering ? `${g.lines.filter((l) => l.quantity > 0).length} item${g.lines.filter((l) => l.quantity > 0).length === 1 ? '' : 's'}` : 'nothing from them this time'}</span>
                {g.drafts.length > 0 && <span className="text-[13px] text-signal-warn">has a draft already: {g.drafts.map((d) => d.number).join(', ')}</span>}
                <span className="ml-auto font-mono font-semibold tabular-nums text-ink">{money(g.total)}</span>
              </div>
              {g.lines.map((l) => {
                const p = againstPar(l.item);
                const pack = packOf(l.item);
                return (
                  <div key={l.stock_id} className={`grid grid-cols-[minmax(0,1fr)_auto_auto_auto] items-center gap-3 border-b border-hairline px-4 py-2 last:border-0 sm:grid-cols-[minmax(0,1fr)_136px_80px_32px] ${l.quantity > 0 ? '' : 'bg-surface-2/60'}`} data-testid={`builder-line-${l.stock_id}`} data-zero={l.quantity > 0 ? undefined : '1'}>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 text-[14px] text-ink"><span className="min-w-0 truncate" title={l.item.name}>{l.item.name}</span>{p.low && <Signal tone="stop" dot={false} className="shrink-0">low</Signal>}</span>
                      <span className="block font-mono text-[12px] tabular-nums text-ink-faint">
                        {p.par ? `${qty(p.have, '')} of ${amount(p.par, l.item.unit)}` : `${amount(p.have, l.item.unit)} · no par`}{pack > 1 ? ` · packs of ${pack}` : ''}
                      </span>
                      {onOrder[l.stock_id] && (
                        <span className="block text-[12px] text-ink-soft" data-testid={`builder-on-order-${l.stock_id}`}>
                          {onOrderWords(onOrder[l.stock_id].quantity, l.item.unit, onOrder[l.stock_id].due)}{p.par && p.have + onOrder[l.stock_id].quantity >= p.par ? ' · already covered' : ''}
                        </span>
                      )}
                    </span>
                    <span className="flex h-9 items-center overflow-hidden rounded-md border border-hairline-strong bg-white">
                      <button type="button" className="h-full w-8 bg-hover-tint font-bold text-verlo" onClick={() => less(l)} aria-label={`Less ${l.item.name}`}>−</button>
                      <span className="flex-1 whitespace-nowrap px-1 text-center font-mono text-[13px] tabular-nums">{pack > 1 ? `${Math.round(l.quantity / pack * 100) / 100} × ${pack}` : amount(l.quantity, l.item.unit)}</span>
                      <button type="button" className="h-full w-8 bg-hover-tint font-bold text-verlo" onClick={() => setBasket((b) => step(b, l.item, 1))} aria-label={`More ${l.item.name}`}>+</button>
                    </span>
                    {l.quantity > 0
                      ? <span className="text-right font-mono text-[13.5px] tabular-nums text-ink">{money(l.cost)}</span>
                      : <span className="text-right text-[12px] text-ink-faint">on order</span>}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-ink-faint" aria-label={`More for ${l.item.name}`} data-testid={`builder-more-${l.stock_id}`}><MoreHorizontal size={16} /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-72">
                        <DropdownMenuItem onSelect={() => setBasket((b) => remove(b, l.stock_id))} data-testid="builder-remove">
                          <span>Remove from this order<span className="block text-[12px] text-ink-faint">Just this time</span></span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setStopping({ item: l.item, to: '' })} data-testid="builder-stop-buying">
                          <span>Stop buying it from {g.supplier_name}<span className="block text-[12px] text-ink-faint">Not suggested from them again</span></span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => setUnstocking(l.item)} className="text-signal-stop" data-testid="builder-stop-stocking">
                          <span>Stop stocking it<span className="block text-[12px] text-ink-faint">Off the to-order list altogether</span></span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
              <div className="flex flex-wrap gap-4 border-t border-hairline bg-surface-2 px-4 py-2 text-[13px] font-semibold text-verlo">
                <AddFrom supplier={supById[g.supplier_id]} stock={stock} basket={basket} onAdd={addItem} onNew={() => newProduct(g.supplier_id)}
                  trigger={<button type="button" className="hover:underline" data-testid={`builder-add-from-${g.supplier_id}`}>+ Add from {g.supplier_name}</button>} />
                <button type="button" className="hover:underline" onClick={() => newProduct(g.supplier_id)} data-testid={`builder-new-${g.supplier_id}`}>+ New product</button>
              </div>
            </section>
          ))}

          {view.orphans.length > 0 && (
            <Notice tone="warn" word="No supplier">
              {view.orphans.map((o) => o.item?.name || 'An item').join(', ')} {view.orphans.length === 1 ? 'has' : 'have'} nobody to order from, so {view.orphans.length === 1 ? "it isn't" : "they aren't"} drafted. Give {view.orphans.length === 1 ? 'it' : 'them'} a supplier in Stock levels.
            </Notice>
          )}
        </div>

        <aside className="self-start rounded-lg border border-hairline bg-white p-4 xl:sticky xl:top-4" data-testid="builder-summary">
          <h2 className="font-heading text-[15px] font-extrabold text-ink">{n ? `${n} draft${n === 1 ? '' : 's'}, one per supplier` : 'No drafts yet'}</h2>
          <div className="mt-2 space-y-1.5">
            {sending.map((g) => (
              <div key={g.supplier_id} className="flex justify-between gap-3 text-[13.5px]"><span className="truncate text-ink-soft">{g.supplier_name}</span><span className="font-mono tabular-nums text-ink">{money(g.total)}</span></div>
            ))}
          </div>
          <div className="mt-3 flex justify-between border-t border-hairline-strong pt-3 text-[15px] font-bold text-ink"><span>Total, ex VAT</span><span className="font-mono tabular-nums" data-testid="builder-total">{money(view.total)}</span></div>
          <Button className="mt-3 w-full bg-verlo hover:bg-verlo-hover" disabled={!n || busy || !authoring.ready} title={authoring.reason || undefined} onClick={() => create(null, 'send')} data-testid="builder-review-send">
            {busy && then === 'send' ? 'Getting them ready…' : `Review and send ${n || ''} order${n === 1 ? '' : 's'}`}
          </Button>
          <Button variant="outline" className="mt-2 w-full" disabled={!n || busy || !authoring.ready} onClick={() => create(null, 'save')} data-testid="builder-create">
            {busy && then === 'save' ? 'Saving…' : 'Save as drafts for later'}
          </Button>
          <p className="mt-2 text-[12px] leading-snug text-ink-soft">Review and send shows each supplier's delivery day and minimum, then emails them all. Nothing goes until you press Send there.</p>
          {basket.lines.length > 0 && (
            <Button variant="outline" size="sm" className="mt-3 w-full text-ink-soft" onClick={() => { setBasket(emptyBasket()); setCarriedOn(false); }} data-testid="builder-clear">Clear and start again</Button>
          )}
        </aside>
      </div>

      {/* Stop buying it from them */}
      <Dialog open={!!stopping} onOpenChange={(o) => { if (!o) setStopping(null); }}>
        <DialogContent className="max-w-md" data-testid="builder-stop-buying-dialog">
          {stopping && (<>
            <DialogHeader>
              <DialogTitle>Stop buying {stopping.item.name} from {supById[stopping.item.supplier_id]?.name}?</DialogTitle>
              <DialogDescription>
                It won&apos;t be suggested from them again. It stays in stock and in its recipes. Until you give it another supplier, it waits under No supplier on the to-order list.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              <Label className="text-[12.5px] text-ink-soft">Buy it from someone else instead (optional)</Label>
              <Select value={stopping.to || 'none'} onValueChange={(v) => setStopping({ ...stopping, to: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nobody for now</SelectItem>
                  {suppliers.filter((s) => s.id !== stopping.item.supplier_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {stopping.to && <p className="text-[12px] text-ink-faint">It moves to {supById[stopping.to]?.name} on this order, at the price you have for it now ({money(stopping.item.cost_per_unit)} / {stopping.item.unit}). Change it on their draft if theirs differs.</p>}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStopping(null)}>Cancel</Button>
              <Button className="bg-verlo hover:bg-verlo-hover" disabled={busy} onClick={stopBuying} data-testid="builder-stop-buying-confirm">
                {stopping.to ? `Buy it from ${supById[stopping.to]?.name}` : `Stop buying from ${supById[stopping.item.supplier_id]?.name}`}
              </Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Stop stocking it */}
      <Dialog open={!!unstocking} onOpenChange={(o) => { if (!o) setUnstocking(null); }}>
        <DialogContent className="max-w-md" data-testid="builder-stop-stocking-dialog">
          {unstocking && (<>
            <DialogHeader>
              <DialogTitle>Stop stocking {unstocking.name}?</DialogTitle>
              <DialogDescription>
                Its par goes to 0, so it&apos;s never on the to-order list. The {qty(unstocking.quantity, unstocking.unit)} you have stays counted until it&apos;s used or wasted. The till still sells it while there&apos;s some, unless you take it off the menu.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setUnstocking(null)}>Cancel</Button>
              <Button variant="outline" className="border-signal-stop-line text-signal-stop hover:text-signal-stop" disabled={busy} onClick={stopStocking} data-testid="builder-stop-stocking-confirm">Stop stocking it</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* A new product */}
      <Dialog open={!!making} onOpenChange={(o) => { if (!o) setMaking(null); }}>
        <DialogContent className="max-w-lg" data-testid="builder-new-dialog">
          {making && (<>
            <DialogHeader>
              <DialogTitle>{making.supplier_id ? `New from ${supById[making.supplier_id]?.name}` : 'A new product'}</DialogTitle>
              <DialogDescription>It goes into stock at 0, bought from them, and onto this order. It isn&apos;t on the menu until you add it to a dish or a drink.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-x-3 gap-y-3">
              <Field label="Name" wide><Input value={making.name} onChange={(e) => setMaking({ ...making, name: e.target.value })} placeholder="e.g. Picpoul de Pinet, bottle" autoFocus data-testid="builder-new-name" /></Field>
              {!making.supplier_id || !supById[making.supplier_id] ? (
                <Field label="Who it's from" wide>
                  <Select value={making.supplier_id || ''} onValueChange={(v) => setMaking({ ...making, supplier_id: v })}>
                    <SelectTrigger data-testid="builder-new-supplier"><SelectValue placeholder="Pick a supplier" /></SelectTrigger>
                    <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              ) : null}
              <Field label="You count it in">
                <Select value={making.unit} onValueChange={(v) => setMaking({ ...making, unit: v })}>
                  <SelectTrigger data-testid="builder-new-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{[...new Set([...UNITS, ...(stock || []).map((s) => s.unit).filter((u) => u && !/^case of/i.test(u))])].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="They sell it in packs of"><Input type="number" min="1" step="1" value={making.pack_size} onChange={(e) => setMaking({ ...making, pack_size: e.target.value })} className="font-mono" data-testid="builder-new-pack" /></Field>
              <Field label={making.unit === 'each' ? 'Their price, each' : `Their price, a${/^[aeiou]/i.test(making.unit) ? 'n' : ''} ${making.unit}`}><Input type="number" min="0" step="0.01" value={making.price} onChange={(e) => setMaking({ ...making, price: e.target.value })} placeholder="0.00" className="font-mono" data-testid="builder-new-price" /></Field>
              <Field label="Their code (optional)"><Input value={making.order_code} onChange={(e) => setMaking({ ...making, order_code: e.target.value })} className="font-mono" /></Field>
              <Field label="Keep at least (par, optional)">
                <Input type="number" min="0" step="any" value={making.par_level} onChange={(e) => setMaking({ ...making, par_level: e.target.value })} placeholder="Set it later" className="font-mono" data-testid="builder-new-par" />
              </Field>
              <Field label="Stock section">
                <Select value={making.category || 'General'} onValueChange={(v) => setMaking({ ...making, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{[...new Set(['General', ...categories])].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Order now" wide>
                <span className="flex h-9 w-56 items-center overflow-hidden rounded-md border border-hairline-strong bg-white">
                  <button type="button" className="h-full w-8 bg-hover-tint font-bold text-verlo" onClick={() => setMaking({ ...making, order: Math.max(1, making.order - 1) })} aria-label="One pack fewer">−</button>
                  <span className="flex-1 text-center font-mono text-[13px] tabular-nums">{(parseFloat(making.pack_size) || 1) > 1 ? `${making.order} × ${parseFloat(making.pack_size)}` : amount(making.order, making.unit)}</span>
                  <button type="button" className="h-full w-8 bg-hover-tint font-bold text-verlo" onClick={() => setMaking({ ...making, order: making.order + 1 })} aria-label="One pack more">+</button>
                </span>
              </Field>
            </div>
            <p className="text-[12px] text-ink-faint">With no par it won&apos;t come up on its own when it runs low. You can set one later on Stock levels.</p>
            <DialogFooter className="items-center sm:justify-between">
              <span className="font-mono font-semibold tabular-nums text-ink">{money((parseFloat(making.price) || 0) * making.order * (parseFloat(making.pack_size) || 1))}</span>
              <span className="flex gap-2">
                <Button variant="ghost" onClick={() => setMaking(null)}>Cancel</Button>
                <Button className="bg-verlo hover:bg-verlo-hover" disabled={busy} onClick={makeProduct} data-testid="builder-new-add">Add to this order</Button>
              </span>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Where a supplier already has a draft open: add to it, or start another. Asked, every time. */}
      <Dialog open={!!asking} onOpenChange={(o) => { if (!o) setAsking(null); }}>
        <DialogContent className="max-w-lg" data-testid="builder-ask-dialog">
          {asking && (<>
            <DialogHeader>
              <DialogTitle>{Object.keys(asking).length === 1 ? 'A supplier already has a draft' : 'Some suppliers already have a draft'}</DialogTitle>
              <DialogDescription>Add these to it, or start another? A draft might be half-checked, so it&apos;s your call.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {view.groups.filter((g) => asking[g.supplier_id] !== undefined).map((g) => (
                <div key={g.supplier_id} className="space-y-1.5" data-testid={`builder-ask-${g.supplier_id}`}>
                  <p className="text-[14px] font-semibold text-ink">{g.supplier_name} <span className="font-normal text-ink-soft">· {g.lines.length} item{g.lines.length === 1 ? '' : 's'}, {money(g.total)}</span></p>
                  <Select value={asking[g.supplier_id]} onValueChange={(v) => setAsking({ ...asking, [g.supplier_id]: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {g.drafts.map((d) => <SelectItem key={d.id} value={d.id}>Add to {d.number} ({money(d.total)}, started {new Date(d.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})</SelectItem>)}
                      <SelectItem value="new">Start a new draft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setAsking(null)}>Back to the order</Button>
              <Button className="bg-verlo hover:bg-verlo-hover" disabled={busy} onClick={() => create(asking, then)} data-testid="builder-ask-confirm">{busy ? 'Working…' : 'Carry on'}</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// A supplier's products, to add from: what you have of each, the ones on the order ticked.
function AddFrom({ supplier, stock, basket, onAdd, onNew, trigger }) {
  if (!supplier) return null;
  const on = new Set(basket.lines.map((l) => l.stock_id));
  const theirs = (stock || []).filter((s) => s.supplier_id === supplier.id).sort((a, b) => a.name.localeCompare(b.name));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 w-80 overflow-y-auto">
        {theirs.length === 0 && <p className="px-3 py-2 text-[13px] text-ink-faint">Nothing of theirs in stock yet.</p>}
        {theirs.map((s) => (
          <DropdownMenuItem key={s.id} onSelect={() => onAdd(s)} data-testid={`builder-from-${s.id}`}>
            <span className="flex w-full items-center justify-between gap-3">
              <span className="min-w-0"><span className="block truncate">{s.name}</span><span className="block text-[12px] text-ink-faint">you have {s.par_level ? `${qty(s.quantity, '')} of ${amount(s.par_level, s.unit)}` : amount(s.quantity, s.unit)}</span></span>
              {on.has(s.id) ? <span className="text-[12px] text-ink-faint">on it · +1</span> : <Plus size={14} className="text-verlo" />}
            </span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onNew} className="font-semibold text-verlo">+ New product from {supplier.name}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Field({ label, wide, children }) {
  return (
    <div className={`space-y-1.5 ${wide ? 'col-span-2' : ''}`}>
      <Label className="text-[12.5px] text-ink-soft">{label}</Label>
      {children}
    </div>
  );
}
